import { CLOUD_DATA_SCHEMA_NOTIFIER, WIX_CMS } from '@wix/app-definition-ids'
import type { Channel, VirtualSocket } from '@wix/duplexer-js'
import { Duplexer } from '@wix/duplexer-js'
import type { DataSettings } from '@wix/wix-data-client-common-standalone'
import { DATA_RESOURCE_USAGE_DUPLEXER_APP_ID } from '@wix/wix-data-client-common-standalone'
import type { ResourceUsage } from '@wix/ambassador-cloud-resourceusage-v1-resource-usage/types'
import type { PageLinkGenerationPayload } from './onPageLinkGeneration'

interface SubscribeParams<T> {
  appDefId: string
  channelName: string
  event: string
  listener: (payload: T) => void
}

type Connections = Record<
  string,
  {
    connection: VirtualSocket
    channels: Record<string, Channel>
  }
>

export interface DuplexerClient {
  onSchemaChange: (listener: () => void) => void
  onPremiumLimitsChange: (listener: (payload: ResourceUsage) => void) => void
  onPageLinkGeneration: (
    listener: (payload: PageLinkGenerationPayload) => void,
  ) => void
  onDataSettingsChange: (
    listener: (payload: DataSettings & { changeType: string }) => void,
  ) => void
}

interface Params {
  getInstance: () => string
}

export const createDuplexerClient = ({
  getInstance,
}: Params): DuplexerClient => {
  const duplexer = new Duplexer('duplexer.wix.com', {
    instanceUpdater: {
      getInstance,
    },
  })

  const connections: Connections = {}

  const getChannel = (appDefId: string, channelName: string): Channel => {
    connections[appDefId] ??= {
      connection: duplexer.connect({ appDefId }),
      channels: {},
    }
    const { connection, channels } = connections[appDefId]
    channels[channelName] ??= connection.subscribe(channelName)
    return channels[channelName]
  }

  const subscribe = <T>({
    appDefId,
    channelName,
    event,
    listener,
  }: SubscribeParams<T>): void => {
    const channel = getChannel(appDefId, channelName)
    channel.on(event, listener)
  }

  return {
    onSchemaChange: listener =>
      subscribe({
        appDefId: CLOUD_DATA_SCHEMA_NOTIFIER,
        channelName: 'wix-data-schema-change-notifications',
        event: 'schema-change',
        listener,
      }),
    onPremiumLimitsChange: listener =>
      subscribe({
        appDefId: DATA_RESOURCE_USAGE_DUPLEXER_APP_ID,
        channelName: 'wix-data-resource-usage-notifications',
        event: 'limits-changed',
        listener,
      }),
    onPageLinkGeneration: listener =>
      subscribe({
        appDefId: WIX_CMS,
        channelName: 'wix-data-pagelink-rendering-notifications',
        event: 'pagelink-rendering-complete',
        listener,
      }),
    onDataSettingsChange: listener =>
      subscribe({
        appDefId: CLOUD_DATA_SCHEMA_NOTIFIER,
        channelName: 'wix-data-settings-change-notifications',
        event: 'data-settings-change',
        listener,
      }),
  }
}
